// ── coaching-write-truth-caches: class-12 write-truth cache patching ─────────
// Extracted from useCoachingQueries.ts (2026-07-06 size split). Doctrine
// (AGENTS.md Hyperdrive law + loop-learnings class 12): mutation responses are
// server truth — PATCH rows into detail/list caches; NEVER invalidate the same
// entity family on a response-row path (a refetch inside Hyperdrive's ~60s
// stale window resurrects pre-write rows). Rowless mutations keep refetch via
// the includeLists options.

import type { QueryClient } from "@tanstack/react-query";
import type {
  CoachingArtifactDto,
  CoachingCohortDto,
  CoachingOperationResult,
  CoachingParticipantDto,
  CoachingResourceDto,
  CoachingUserDto,
  ListResponse
} from "../services/coaching-dtos";
import type {
  CoachingCertificateDto,
  CoachingThresholdDto,
  CoachingThresholdsResponseDto
} from "../services/coaching-readiness-dtos";
import { liveParticipantRow } from "../services/coaching-live-readmodels";
import { coachingQueryKeys } from "./coaching-query-keys";

export async function cacheParticipantLifecycleReadback(
  queryClient: QueryClient,
  participant: CoachingParticipantDto,
  options?: { patchCohortRoster?: boolean }
) {
  if (!participant.id) return;
  queryClient.setQueryData(coachingQueryKeys.participantDetail(participant.id), participant);
  queryClient.setQueriesData(
    { queryKey: [...coachingQueryKeys.root, "list", "participants.list"] },
    (existing: ListResponse<CoachingParticipantDto> | undefined) => {
      if (!existing?.items) return existing;
      if (!existing.items.some((row) => row.id === participant.id)) return existing;
      return {
        ...existing,
        items: existing.items.map((row) => (row.id === participant.id ? { ...row, ...participant } : row))
      };
    }
  );
  queryClient.setQueryData(
    coachingQueryKeys.list("participants.list"),
    (existing: ListResponse<CoachingParticipantDto> | undefined) => {
      if (!existing?.items) return existing;
      if (existing.items.some((row) => row.id === participant.id)) return existing;
      return { ...existing, items: [participant, ...existing.items] };
    }
  );
  if (options?.patchCohortRoster && participant.cohort_id) {
    queryClient.setQueriesData(
      { queryKey: coachingQueryKeys.list("participants.listForCohort", { cohort_id: participant.cohort_id }) },
      (existing: ListResponse<CoachingParticipantDto> | undefined) => {
        if (!existing?.items) return { items: [participant], next_cursor: null };
        if (existing.items.some((row) => row.id === participant.id)) {
          return {
            ...existing,
            items: existing.items.map((row) => (row.id === participant.id ? { ...row, ...participant } : row))
          };
        }
        return { ...existing, items: [participant, ...existing.items] };
      }
    );
    await invalidateParticipantLifecycleReadbacks(queryClient);
    return;
  }
  await invalidateParticipantLifecycleReadbacks(queryClient, participant.cohort_id);
}

/**
 * WRITE-TRUTH removal (class 12): DROP the removed participant from list caches
 * (active-only) instead of invalidating — a stale refetch would resurrect the
 * row for ~60s. Its own detail lookup (`participants.list` by id) keeps the
 * row, marked `removed`, so the detail page stays readable.
 */
export async function cacheParticipantRemoval(
  queryClient: QueryClient,
  result: CoachingOperationResult,
  cohortId?: string,
  participantId?: string
) {
  const removedRecord = result.participant;
  const removedId = String(
    (removedRecord && typeof removedRecord === "object" ? removedRecord.id : undefined) ?? participantId ?? ""
  );
  if (removedId && result.ok) {
    const detailKey = coachingQueryKeys.list("participants.list", { id: removedId });
    const detail = queryClient.getQueryData<ListResponse<CoachingParticipantDto>>(detailKey);
    for (const family of ["participants.list", "participants.listForCohort"]) {
      queryClient.setQueriesData(
        { queryKey: [...coachingQueryKeys.root, "list", family] },
        (existing: ListResponse<CoachingParticipantDto> | undefined) =>
          existing?.items?.some((row) => row.id === removedId)
            ? { ...existing, items: existing.items.filter((row) => row.id !== removedId) }
            : existing
      );
    }
    queryClient.removeQueries({ queryKey: coachingQueryKeys.participantDetail(removedId) });
    if (detail?.items?.some((row) => row.id === removedId)) {
      queryClient.setQueryData<ListResponse<CoachingParticipantDto>>(detailKey, {
        ...detail,
        items: detail.items.map((row) => (row.id === removedId ? { ...row, status: "removed" } : row))
      });
    }
  }
  // NOTE cohortId deliberately NOT forwarded: the cohort roster list was just
  // patched from the response — refetching it inside the stale window would
  // resurrect the removed row. Unrelated families still refresh.
  await invalidateParticipantLifecycleReadbacks(queryClient);
}

export interface ScopedParticipantRemoval {
  cohortId: string;
  participantId?: string;
  orgId?: string | null;
}

/**
 * Z6 removal write-truth with explicit membership scope. Unlike the legacy
 * helper above, this never removes by participant id alone: cohort roster rows
 * must match the target cohort and org, while org-level rows without enough
 * membership identity are invalidated instead of being dropped.
 */
export async function cacheScopedParticipantRemoval(
  queryClient: QueryClient,
  result: CoachingOperationResult,
  scope: ScopedParticipantRemoval
) {
  const removedRecord = result.participant;
  const removedId = String(
    (removedRecord && typeof removedRecord === "object" ? removedRecord.id : undefined) ?? scope.participantId ?? ""
  );
  const targetOrgId = scope.orgId ?? (removedRecord && typeof removedRecord === "object" ? participantOrg(removedRecord) : undefined);

  if (removedId) {
    const participantQueries = queryClient.getQueryCache().findAll({
      predicate: (query) => {
        const parsed = participantListQuery(query.queryKey);
        if (!parsed) return false;
        if (parsed.family === "participants.listForCohort") {
          return parsed.query.cohort_id === scope.cohortId && queryBelongsToOrg(parsed.query, targetOrgId);
        }
        return queryBelongsToOrg(parsed.query, targetOrgId);
      }
    });

    for (const query of participantQueries) {
      const parsed = participantListQuery(query.queryKey);
      const existing = query.state.data as ListResponse<CoachingParticipantDto> | undefined;
      if (!parsed || !existing?.items) continue;
      const matchingRows = existing.items.filter((row) => String(row.id) === removedId);
      const isCohortRoster = parsed.family === "participants.listForCohort";
      if (!isCohortRoster && matchingRows.some((row) => participantCohort(row) === undefined)) {
        await queryClient.invalidateQueries({ queryKey: query.queryKey });
        continue;
      }
      const safeRows = matchingRows.filter((row) =>
        participantBelongsToRemoval(
          row,
          scope,
          targetOrgId,
          isCohortRoster ? parsed.query.cohort_id : undefined,
          parsed.query.orgId ?? parsed.query.org_id
        )
      );
      const unsafeRows = matchingRows.some((row) => !safeRows.includes(row));
      if (unsafeRows) await queryClient.invalidateQueries({ queryKey: query.queryKey });
      if (safeRows.length) {
        queryClient.setQueryData(query.queryKey, {
          ...existing,
          items: existing.items.filter((row) => !safeRows.includes(row))
        });
      }
    }

    const detailQuery = queryClient.getQueryCache().find({ queryKey: coachingQueryKeys.participantDetail(removedId) });
    const detail = detailQuery?.state.data as CoachingParticipantDto | undefined;
    if (detail && participantBelongsToRemoval(detail, scope, targetOrgId)) {
      queryClient.removeQueries({ queryKey: coachingQueryKeys.participantDetail(removedId) });
    }
  }

  await invalidateParticipantLifecycleReadbacks(queryClient);
}

function participantListQuery(queryKey: readonly unknown[]) {
  if (queryKey[0] !== coachingQueryKeys.root[0] || queryKey[1] !== "list") return null;
  const family = queryKey[2];
  if (family !== "participants.list" && family !== "participants.listForCohort") return null;
  const query = queryKey[3];
  return { family, query: query && typeof query === "object" ? query as Record<string, unknown> : {} } as {
    family: "participants.list" | "participants.listForCohort";
    query: Record<string, unknown>;
  };
}

function queryBelongsToOrg(query: Record<string, unknown>, orgId: string | undefined) {
  if (!orgId) return true;
  const queryOrg = query.orgId ?? query.org_id;
  return queryOrg == null || String(queryOrg) === orgId;
}
function participantBelongsToRemoval(
  row: CoachingParticipantDto,
  scope: ScopedParticipantRemoval,
  orgId: string | undefined,
  queryCohortId?: unknown,
  queryOrgId?: unknown
) {
  const rowCohortId = participantCohort(row) ?? (typeof queryCohortId === "string" ? queryCohortId : undefined);
  if (rowCohortId !== scope.cohortId) return false;
  if (!orgId) return true;
  const rowOrgId = participantOrg(row) ?? (typeof queryOrgId === "string" ? queryOrgId : undefined);
  return rowOrgId === orgId;
}

function participantCohort(row: CoachingParticipantDto | Record<string, unknown>) {
  const value = (row as Record<string, unknown>).cohort_id ?? (row as Record<string, unknown>).cohortId;
  return value == null ? undefined : String(value);
}

function participantOrg(row: CoachingParticipantDto | Record<string, unknown>) {
  const value = (row as Record<string, unknown>).org_id ?? (row as Record<string, unknown>).orgId;
  return value == null ? undefined : String(value);
}

export async function invalidateParticipantLifecycleReadbacks(queryClient: QueryClient, cohortId?: string) {
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("history.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.recordScopeRoot() }),
    queryClient.invalidateQueries({ queryKey: [...coachingQueryKeys.root, "dashboard"] })
  ];
  if (cohortId) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: coachingQueryKeys.list("participants.listForCohort", { cohort_id: cohortId })
      })
    );
  }
  await Promise.all(invalidations);
}


/** WRITE-TRUTH (class 12): attach returns a participant row — patch lists; do not invalidate roster. */
export async function cacheParticipantAttachReadback(
  queryClient: QueryClient,
  result: CoachingOperationResult,
  cohortId: string
) {
  const record = result.participant;
  if (!record || typeof record !== "object") {
    await invalidateParticipantLifecycleReadbacks(queryClient, cohortId);
    return;
  }
  const participant = liveParticipantRow(record as Record<string, unknown>);
  if (!participant.cohort_id) participant.cohort_id = cohortId;
  if (!participant.id) {
    await invalidateParticipantLifecycleReadbacks(queryClient, cohortId);
    return;
  }
  await cacheParticipantLifecycleReadback(queryClient, participant, { patchCohortRoster: true });
}

/** WRITE-TRUTH (class 12): assign-membership is rowless — refresh history/dashboard only. */
export async function cacheParticipantAssignMembershipReadback(queryClient: QueryClient) {
  await invalidateParticipantLifecycleReadbacks(queryClient);
}

/** WRITE-TRUTH (class 12): bulk import — patch roster from row_results or refetch when rowless (live). */
export async function cacheParticipantBulkImportReadback(
  queryClient: QueryClient,
  result: CoachingOperationResult,
  cohortId: string
) {
  const rawRows = Array.isArray(result.row_results) ? result.row_results : [];
  const importedRows = rawRows.filter((row) => {
    const status = String((row as Record<string, unknown>).status ?? "");
    return status === "ready" || status === "imported" || status === "ok" || status === "created";
  });

  if (importedRows.length > 0) {
    for (const row of importedRows) {
      const record = row as Record<string, unknown>;
      const participant = liveParticipantRow({
        ...record,
        cohort_id: record.cohort_id ?? record.cohortId ?? cohortId
      });
      if (!participant.id) continue;
      queryClient.setQueryData(coachingQueryKeys.participantDetail(participant.id), participant);
      queryClient.setQueriesData(
        { queryKey: [...coachingQueryKeys.root, "list", "participants.list"] },
        (existing: ListResponse<CoachingParticipantDto> | undefined) => {
          if (!existing?.items) return existing;
          if (existing.items.some((item) => item.id === participant.id)) return existing;
          return { ...existing, items: [participant, ...existing.items] };
        }
      );
      queryClient.setQueriesData(
        { queryKey: coachingQueryKeys.list("participants.listForCohort", { cohort_id: cohortId }) },
        (existing: ListResponse<CoachingParticipantDto> | undefined) => {
          if (!existing?.items) return { items: [participant], next_cursor: null };
          if (existing.items.some((item) => item.id === participant.id)) return existing;
          return { ...existing, items: [participant, ...existing.items] };
        }
      );
    }
    await invalidateParticipantLifecycleReadbacks(queryClient);
    return;
  }

  await invalidateCohortLifecycleReadbacks(queryClient, cohortId, { includeLists: true });
  await queryClient.invalidateQueries({
    queryKey: coachingQueryKeys.list("participants.listForCohort", { cohort_id: cohortId })
  });
}

export async function cacheUserLifecycleReadback(queryClient: QueryClient, user: CoachingUserDto) {
  if (!user.id) return;
  queryClient.setQueryData(coachingQueryKeys.userDetail(user.id), user);
  queryClient.setQueriesData(
    { queryKey: [...coachingQueryKeys.root, "list", "users.list"] },
    (existing: ListResponse<CoachingUserDto> | undefined) => {
      if (!existing?.items) return existing;
      if (!existing.items.some((row) => row.id === user.id)) return existing;
      return {
        ...existing,
        items: existing.items.map((row) => (row.id === user.id ? { ...row, ...user } : row))
      };
    }
  );
  queryClient.setQueryData(
    coachingQueryKeys.list("users.list"),
    (existing: ListResponse<CoachingUserDto> | undefined) => {
      if (!existing?.items) return existing;
      if (existing.items.some((row) => row.id === user.id)) return existing;
      return { ...existing, items: [user, ...existing.items] };
    }
  );
  await invalidateUserLifecycleReadbacks(queryClient);
}

export async function cacheCohortLifecycleReadback(queryClient: QueryClient, cohort: CoachingCohortDto) {
  if (!cohort.id) return;
  queryClient.setQueryData(coachingQueryKeys.cohortDetail(cohort.id), cohort);
  queryClient.setQueriesData(
    { queryKey: [...coachingQueryKeys.root, "list", "cohorts.list"] },
    (existing: ListResponse<CoachingCohortDto> | undefined) => {
      if (!existing?.items) return existing;
      if (!existing.items.some((row) => row.id === cohort.id)) return existing;
      return {
        ...existing,
        items: existing.items.map((row) => (row.id === cohort.id ? { ...row, ...cohort } : row))
      };
    }
  );
  queryClient.setQueryData(
    coachingQueryKeys.list("cohorts.list"),
    (existing: ListResponse<CoachingCohortDto> | undefined) => {
      if (!existing?.items) return existing;
      if (existing.items.some((row) => row.id === cohort.id)) return existing;
      return { ...existing, items: [cohort, ...existing.items] };
    }
  );
  await invalidateCohortLifecycleReadbacks(queryClient);
}

export async function invalidateUserLifecycleReadbacks(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("users.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("history.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.recordScopeRoot() })
  ]);
}

export async function invalidateCohortLifecycleReadbacks(
  queryClient: QueryClient,
  cohortId?: string,
  opts?: { includeLists?: boolean }
) {
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("participants.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("history.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.recordScopeRoot() }),
    queryClient.invalidateQueries({ queryKey: [...coachingQueryKeys.root, "dashboard"] })
  ];
  if (opts?.includeLists) {
    invalidations.push(queryClient.invalidateQueries({ queryKey: [...coachingQueryKeys.root, "list", "cohorts.list"] }));
  }
  if (cohortId) invalidations.push(queryClient.invalidateQueries({ queryKey: coachingQueryKeys.cohortDetail(cohortId) }));
  await Promise.all(invalidations);
}

export async function cacheReportMarkReviewedReadback(
  queryClient: QueryClient,
  result: CoachingOperationResult
) {
  const reportId = result.review?.reportId ?? result.id;
  if (!reportId) return;
  const reviewedAt = result.review?.reviewedAt ?? null;
  const approvedAt = result.review?.approvedAt ?? null;
  const patch = (row: CoachingArtifactDto): CoachingArtifactDto => ({
    ...row,
    reviewed_at: reviewedAt ?? row.reviewed_at ?? null,
    approved_at: approvedAt ?? row.approved_at ?? null
  });
  queryClient.setQueryData(coachingQueryKeys.reportDetail(reportId), (existing: CoachingArtifactDto | undefined) =>
    existing ? patch(existing) : existing
  );
  queryClient.setQueriesData(
    { queryKey: [...coachingQueryKeys.root, "list", "reports.list"] },
    (existing: ListResponse<CoachingArtifactDto> | undefined) => {
      if (!existing?.items) return existing;
      if (!existing.items.some((row) => row.id === reportId)) return existing;
      return { ...existing, items: existing.items.map((row) => (row.id === reportId ? patch(row) : row)) };
    }
  );
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.analytics.root() }),
    queryClient.invalidateQueries({ queryKey: [...coachingQueryKeys.root, "dashboard"] }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.recordScopeRoot() })
  ]);
}

export async function cacheResourceLifecycleReadback(
  queryClient: QueryClient,
  resourceId: string,
  resource: CoachingResourceDto
) {
  if (!resourceId) return;
  queryClient.setQueryData(coachingQueryKeys.resourceDetail(resourceId), resource);
  queryClient.setQueriesData(
    { queryKey: [...coachingQueryKeys.root, "list", "resources.list"] },
    (existing: ListResponse<CoachingResourceDto> | undefined) => {
      if (!existing?.items) return existing;
      if (!existing.items.some((row) => row.id === resourceId)) return existing;
      return {
        ...existing,
        items: existing.items.map((row) => (row.id === resourceId ? { ...row, ...resource } : row))
      };
    }
  );
  queryClient.setQueryData(
    coachingQueryKeys.list("resources.list"),
    (existing: ListResponse<CoachingResourceDto> | undefined) => {
      if (!existing?.items) return existing;
      if (existing.items.some((row) => row.id === resourceId)) return existing;
      return { ...existing, items: [resource, ...existing.items] };
    }
  );
  await invalidateArtifactReadbacks(queryClient, resourceId, { includeLists: false });
}

export async function invalidateArtifactReadbacks(
  queryClient: QueryClient,
  resourceId?: string,
  opts?: { includeLists?: boolean }
) {
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("history.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.dashboard("admin") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.recordScopeRoot() })
  ];
  if (opts?.includeLists !== false) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("reports.list") }),
      queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("resources.list") })
    );
  }
  if (resourceId) invalidations.push(queryClient.invalidateQueries({ queryKey: coachingQueryKeys.resourceDetail(resourceId) }));
  await Promise.all(invalidations);
}

/** WRITE-TRUTH (class 12): threshold upsert returns a row — merge into thresholds list; do not invalidate. */
export function cacheThresholdUpsertReadback(queryClient: QueryClient, threshold: CoachingThresholdDto) {
  const key = coachingQueryKeys.readiness.thresholds();
  queryClient.setQueryData<CoachingThresholdsResponseDto>(key, (existing) => {
    const rows = existing?.thresholds ?? [];
    const idx = rows.findIndex((row) => row.key === threshold.key);
    const next =
      idx >= 0 ? rows.map((row, i) => (i === idx ? threshold : row)) : [...rows, threshold];
    return { thresholds: next };
  });
}

/** WRITE-TRUTH (class 12): certificate issue returns a row — prepend to list; do not invalidate. */
export function cacheCertificateIssueReadback(queryClient: QueryClient, certificate: CoachingCertificateDto) {
  const key = coachingQueryKeys.readiness.certificates();
  queryClient.setQueryData<{ certificates: CoachingCertificateDto[] }>(key, (existing) => {
    const rows = existing?.certificates ?? [];
    if (rows.some((row) => row.id === certificate.id)) {
      return { certificates: rows.map((row) => (row.id === certificate.id ? certificate : row)) };
    }
    return { certificates: [certificate, ...rows] };
  });
}

